import React, { useEffect, useState } from "react";
import CrudService from "services/cruds-service";
import DataTable from "examples/Tables/DataTable";
import { Tooltip } from "@mui/material";
import SyncIcon  from "@mui/icons-material/Sync";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import dayjs from "dayjs";
import { appConfirm } from "components/AppDialog";

import { CrmRowActions, ListIconButton, ListLink, CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, LoadTypeTabs } from "../crm/shared/CrmUi";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import Add from "@mui/icons-material/Add";
const WeeklyShippingTable = () => {
    const [weeklyShippings, setWeeklyShippings] = useState([]);
    const [customerMap, setCustomerMap] = useState({});
    const [tabValue, setTabValue] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchWeeklyShippings = async () => {
            try {
                const response = await CrudService.getWeeklyShippings();
                setWeeklyShippings(response.data);
            } catch (error) {
                console.error("Error fetching weekly shippings:", error);
            }
        };

        const fetchCustomers = async () => {
            try {
                const response = await CrudService.getCustomers();
                const map = {};
                response.data.forEach((c) => {
                    map[c.id] = c.attributes.name;
                });
                setCustomerMap(map);
            } catch (error) {
                console.error("Error fetching customers:", error);
            }
        };

        fetchWeeklyShippings();
        fetchCustomers();
    }, []);

    const handleEdit = (id) => {
        navigate(`/weekly-shipping-order/create-weekly-shipment/details?id=${id}`);
    };

    const handleDelete = async (id) => {
        if (!await appConfirm("Are you sure you want to delete this record?")) return;
        try {
            await CrudService.deleteWeeklyShipping(id);
            const updatedList = weeklyShippings.filter((item) => item.id !== id);
            setWeeklyShippings(updatedList);
        } catch (error) {
            console.error("Error deleting weekly shipping:", error);
        }
    };

    const formatDate = (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-");

    const statusColors = {
        Organized: "#FFEB3B",
        "On the Way": "#2196F3",
        Delivered: "#4CAF50",
    };

    const getProfitColor = (profit) => {
        if (profit > 0) return "#4CAF50";
        if (profit < 0) return "#F44336";
        return "#FFEB3B";
    };

    const columns = [
        {
            Header: "File No",
            accessor: "fileNo",
            Cell: ({ row }) => (
                <ListLink onClick={() => handleEdit(row.original.id)}>
                    {row.original.fileNo}
                </ListLink>
            ),
        },
        {
            Header: "Customer",
            accessor: "customer",
            Cell: ({ value }) => (
                <Tooltip title={value}>
                    <span>{value.length > 25 ? value.slice(0, 25) + "..." : value}</span>
                </Tooltip>
            ),
        },
        { Header: "Pickup Date", accessor: "pickupDate" },
        { Header: "Delivery Date", accessor: "deliveryDate" },
        { Header: "LDM", accessor: "ldm" },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <span
                    style={{
                        backgroundColor: statusColors[value] || "#e0e0e0",
                        color: "#000",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontWeight: 500,
                    }}
                >
                    {value}
                </span>
            ),
        },
        {
            Header: "Selling Rate",
            accessor: "sellingRate",
            Cell: ({ row }) => `${row.original.sellingRate} ${row.original.sellingCurrency}`,
        },
        {
            Header: "Profit",
            accessor: "grossProfit",
            Cell: ({ row }) => (
                <span style={{ color: getProfitColor(Number(row.original.grossProfit)), fontWeight: 500 }}>
                    {row.original.grossProfit} {row.original.sellingCurrency}
                </span>
            ),
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => (
                <CrmRowActions onEdit={() => handleEdit(row.original.id)} editTitle="Edit" onDelete={() => handleDelete(row.original.id)} deleteTitle="Delete"><ListIconButton title="Process" onClick={() => navigate("/shipping-order/create-shipment/details", { state: buildProcessPayload(row.original.raw) })}><SyncIcon /></ListIconButton></CrmRowActions>
            ),
        },
    ];
    const generateFileNo = (index) => {
        const number = index + 1;
        return `FL${number.toString().padStart(5, "0")}`;
    };
    const mapRows = (items) =>
        items.map((item, index) => ({
            id: item.id,
            fileNo: generateFileNo(index),
            customer: customerMap[item.attributes.customerId] || item.attributes.customerId,
            pickupDate: formatDate(item.attributes.pickupDate),
            deliveryDate: formatDate(item.attributes.deliveryDate),
            ldm: item.attributes?.ltlDetails?.[0]?.ldm || "-",
            status: item.attributes.status || "-",
            sellingRate: item.attributes?.financials?.sellingRate || "-",
            sellingCurrency: item.attributes?.financials?.sellingCurrency || "-",
            grossProfit: item.attributes?.financials?.grossProfit || "-",
            raw: item.attributes,
        }));

    // Weekly shipping attribute'lerini CreateShipment sayfasının beklediği alan adlarına dönüştürür
    const buildProcessPayload = (attr = {}) => {
        const financials = attr.financials || {};
        return {
            customer: customerMap[attr.customerId] || "",
            serviceProvider: attr.serviceProvider || "",
            shipper: attr.shipper || "",
            consignee: attr.consignee || "",
            transitTime: attr.transitTime || "",
            plateNumber: attr.plateNumber || "",
            week: attr.week || "",
            roadTransportType: attr.roadTransportType || "",
            pickupDate: attr.pickupDate ? dayjs(attr.pickupDate).format("DD-MM-YYYY") : "",
            deliveryDate: attr.deliveryDate ? dayjs(attr.deliveryDate).format("DD-MM-YYYY") : "",
            departureDate: attr.departureDate ? dayjs(attr.departureDate).format("DD-MM-YYYY") : "",
            sellingRate: financials.sellingRate || "",
            sellingCurrency: financials.sellingCurrency || "",
            grossProfit: financials.grossProfit || 0,
        };
    };

    const ftlRows = mapRows(
        weeklyShippings.filter((item) => item.attributes.roadTransportType === "FTL")
    );

    const ltlRows = mapRows(
        weeklyShippings.filter((item) => item.attributes.roadTransportType === "LTL")
    );

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Weekly Shippings"
                    subtitle="Recurring weekly road shipments. Open a record to edit it, or sync it to create the week's shipment."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={() => navigate("/weekly-shipping-order/create-weekly-shipment/details")}>
                            New Weekly Shipping
                        </CrmActionButton>
                    }
                />

                <LoadTypeTabs

                    options={[

                        { value: 0, label: "FTL", caption: "Full truck load", count: ftlRows.length, icon: <LocalShippingOutlined /> },

                        { value: 1, label: "LTL", caption: "Part load / groupage", count: ltlRows.length, icon: <Inventory2Outlined /> },

                    ]}

                    value={tabValue}

                    onChange={(value) => setTabValue(value)}

                />

                <CrmTableCard
                    title={tabValue === 0 ? "FTL weekly shippings" : "LTL weekly shippings"}
                    summary={`${tabValue === 0 ? ftlRows.length : ltlRows.length} records`}
                >
                    {tabValue === 0 && <DataTable table={{ columns, rows: ftlRows }} canSearch />}
                    {tabValue === 1 && <DataTable table={{ columns, rows: ltlRows }} canSearch />}
                </CrmTableCard>
            </CrmPage>
            <Footer />
        </DashboardLayout>
    );
};

export default WeeklyShippingTable;
